/**
 * Council Trail v0.1 Item 8 — ProposalsChip.
 *
 * Lives in TrailheadHeader row-2 (council-mode chrome). Shows the count
 * of pending proposals for the session; clicking opens the review dialog
 * (operator approves/dismisses inline).
 *
 * Hidden when count === 0 so it doesn't add ambient chrome noise during
 * solo work or when the queue is empty. The chip is the "operator
 * attention surface" — count > 0 means peers want a decision.
 *
 * Click handler is owned by parent; chip stays presentation-only.
 */
import React from 'react';

export interface ProposalsChipProps {
  pendingCount: number;
  onClick: () => void;
}

export function ProposalsChip({ pendingCount, onClick }: ProposalsChipProps) {
  if (pendingCount <= 0) return null;
  return (
    <button
      type="button"
      data-testid="proposals-chip"
      onClick={onClick}
      aria-label={`Review ${pendingCount} pending peer proposal${pendingCount === 1 ? '' : 's'}`}
      className="text-xs px-2 py-0.5 rounded inline-flex items-center gap-1.5
                 bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200
                 border border-violet-300 dark:border-violet-700
                 hover:bg-violet-200 dark:hover:bg-violet-900/60
                 transition-colors"
    >
      <span aria-hidden className="text-sm leading-none">✻</span>
      <span className="tabular-nums font-medium">{pendingCount}</span>
      <span>{pendingCount === 1 ? 'proposal' : 'proposals'}</span>
    </button>
  );
}
